import {
  createMatterScheduledMessage,
  deleteMatterScheduledMessage,
  type MatterSettings,
  updateMatterScheduledMessage,
} from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError, type APIResponse } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import dayjs from "dayjs";
import toast from "react-hot-toast";

export const useCreateMatterScheduledMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterScheduledMessage>[0]) =>
      createMatterScheduledMessage(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              scheduledMessages: [
                ...oldData.data.scheduledMessages,
                {
                  ...data,
                  dateCreated: dayjs().toISOString().replace("Z", ""),
                },
              ],
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      toast.error("Error creating scheduled message");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      toast.success("Scheduled message created successfully");
    },
  });
};

export const useUpdateMatterScheduledMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterScheduledMessage>[0]) =>
      updateMatterScheduledMessage(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              scheduledMessages: oldData.data.scheduledMessages.map(
                (message) => {
                  return message.id === data.id
                    ? {
                        ...message,
                        ...data,
                        dateModified: dayjs().toISOString().replace("Z", ""),
                      }
                    : message;
                },
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating scheduled message");
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      toast.success("Scheduled message updated successfully");
    },
  });
};

export const useDeleteMatterScheduledMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterScheduledMessage>[0]) =>
      deleteMatterScheduledMessage(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              scheduledMessages: oldData.data.scheduledMessages.filter(
                (message) => {
                  return message.id !== data.id;
                },
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      toast.error("Error deleting scheduled message");
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      toast.success("Scheduled message deleted successfully");
    },
  });
};
